import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
} from "react";
import { IAuthParams, ILoggedInUser } from "../apis/auth/auth.interfaces";
import { useAuthUserHook } from "../hooks/useAuthUserHook";
import {
  localStorageGet,
  localStorageSet,
  localStorageDelete,
} from "../utils/localStorage";

type TAuthUserAppState = {
  errorMessage?: string;
  isLoading: boolean;
};

enum ACTION_TYPE {
  SET_USER_DETAILS = "SET_USER_DETAILS",
  RESET_USER_DETAILS = "RESET_USER_DETAILS",
  SET_USER_PARAMS = "SET_USER_PARAMS",
  FETCHING_DATA = "FETCHING_DATA",
  FETCH_DATA_FAILURE = "FETCH_DATA_FAILURE",
}

type Action =
  | { type: ACTION_TYPE.SET_USER_DETAILS; payload: ILoggedInUser }
  | { type: ACTION_TYPE.SET_USER_PARAMS; payload: IAuthParams }
  | { type: ACTION_TYPE.RESET_USER_DETAILS }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialAuthUserState: TAuthUserAppState = {
  errorMessage: undefined,
  isLoading: false,
};

type TState = {
  loggedUserAppState: TAuthUserAppState;
  userDetails: ILoggedInUser | null;
  userParams: IAuthParams | null;
  isAuthenticated: boolean;
};

/** Single root context avoids triple-nested Provider edge cases (e.g. HMR / mismatched Consumers). */
type TAuthRootContextValue = TState & {
  setUserDetails: (user: ILoggedInUser | null) => void;
  setUserParams: (params: IAuthParams | null) => void;
  dispatch: Dispatch<Action>;
};

const AuthRootContext = createContext<TAuthRootContextValue | undefined>(
  undefined,
);

const AuthUserReducer = (
  appState: TAuthUserAppState,
  action: Action,
): TAuthUserAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_USER_DETAILS:
      return { ...appState, isLoading: false, errorMessage: undefined };
    case ACTION_TYPE.RESET_USER_DETAILS:
      return { ...appState, isLoading: false, errorMessage: undefined };
    case ACTION_TYPE.SET_USER_PARAMS:
      return { ...appState };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: "" };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    default:
      throw new Error("Unhandled action type");
  }
};

export const AuthUserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [loggedUserAppState, dispatch] = useReducer(
    AuthUserReducer,
    initialAuthUserState,
  );
  const [userDetails, setUserDetailsState] = useState<ILoggedInUser | null>(
    () => {
      const token = localStorageGet("token");
      const storedUser = localStorageGet("userDetails");
      if (token && storedUser) {
        return storedUser as ILoggedInUser;
      }
      return null;
    },
  );
  const [userParams, setUserParamsState] = useState<IAuthParams | null>(null);

  const setUserDetails = useCallback((user: ILoggedInUser | null) => {
    setUserDetailsState(user);
    if (user) {
      localStorageSet("token", user.token || user.jwtToken || user.apiToken);
      localStorageSet("userDetails", user);
    }
  }, []);

  const setUserParams = useCallback((params: IAuthParams | null) => {
    setUserParamsState(params);
  }, []);

  const currentState = useMemo<TState>(() => {
    const token = localStorageGet("token");
    const storedUserRaw = localStorageGet("userDetails");
    const persistedUser =
      storedUserRaw && typeof storedUserRaw === "object"
        ? (storedUserRaw as ILoggedInUser)
        : null;
    // After login, localStorage is updated synchronously in setUserDetails before React
    // commits userDetails; ProtectedRoute must not treat that as logged out.
    const effectiveUserDetails = userDetails ?? persistedUser;
    return {
      loggedUserAppState,
      userDetails: effectiveUserDetails,
      userParams,
      isAuthenticated: Boolean(token && effectiveUserDetails),
    };
  }, [loggedUserAppState, userDetails, userParams]);

  const rootValue = useMemo<TAuthRootContextValue>(
    () => ({
      ...currentState,
      setUserDetails,
      setUserParams,
      dispatch,
    }),
    [currentState, setUserDetails, setUserParams, dispatch],
  );

  return (
    <AuthRootContext.Provider value={rootValue}>
      {children}
    </AuthRootContext.Provider>
  );
};

export const useAuthUserState = (): TState => {
  const ctx = useContext(AuthRootContext);
  if (ctx === undefined) {
    throw new Error("useAuthUserState must be used within a AuthUserProvider");
  }
  const {
    dispatch: _d,
    setUserDetails: _sd,
    setUserParams: _sp,
    ...state
  } = ctx;
  void _d;
  void _sd;
  void _sp;
  return state;
};

export const useAuthUserActions = () => {
  const ctx = useContext(AuthRootContext);

  const dispatch = ctx?.dispatch;
  const setUserDetailsFromCtx = ctx?.setUserDetails;

  const onLoginSuccess = useCallback(
    (data: ILoggedInUser) => {
      if (!dispatch || !setUserDetailsFromCtx) return;
      dispatch({ type: ACTION_TYPE.SET_USER_DETAILS, payload: data });
      setUserDetailsFromCtx(data);
    },
    [dispatch, setUserDetailsFromCtx],
  );

  const onLoginError = useCallback(
    (error: unknown) => {
      if (!dispatch) return;
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message || err?.message || "Login failed";
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: errorMessage,
      });
    },
    [dispatch],
  );

  const authoriseUserDetails = useAuthUserHook({
    onSuccess: onLoginSuccess,
    onError: onLoginError,
  });

  if (ctx === undefined || !dispatch || !setUserDetailsFromCtx) {
    throw new Error("useAuthUserActions must be within a AuthUserProvider");
  }

  const { setUserDetails } = ctx;

  const loginUserApiCall = async (params: IAuthParams) => {
    dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    dispatch({ type: ACTION_TYPE.SET_USER_PARAMS, payload: params });
    try {
      await authoriseUserDetails.mutateAsync(params);
    } finally {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    }
  };

  const logout = () => {
    dispatch({ type: ACTION_TYPE.RESET_USER_DETAILS });
    localStorageDelete("token");
    localStorageDelete("userDetails");
    setUserDetails(null);
  };

  return { loginUserApiCall, logout };
};
